import { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { ws } from '@/lib/ws';
import type { OrgMeta, UserMeta } from '@shared/protocol';

export interface Session {
  user: UserMeta;
  org: OrgMeta;
  orgs: OrgMeta[];
  switchOrg: (orgId: string) => void;
}

export function AppShell() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Omit<Session, 'switchOrg'> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const switchOrg = useCallback((orgId: string) => {
    ws.send({ type: 'org.select', org_id: orgId });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const offAuth = ws.on('auth.ok', (msg) => {
      if (cancelled) return;
      setSession({ user: msg.user, org: msg.org, orgs: msg.orgs });
    });
    const offSwitch = ws.on('org.switched', (msg) => {
      if (cancelled) return;
      setSession((s) => (s ? { ...s, org: msg.org } : s));
    });
    ws.connect().catch((err) => {
      setError(err instanceof Error ? err.message : 'connection failed');
      navigate('/login');
    });
    return () => {
      cancelled = true;
      offAuth();
      offSwitch();
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }
  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-sm text-muted-foreground">
        Connecting…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar org={session.org} orgs={session.orgs} switchOrg={switchOrg} />
      <div className="flex-1 flex flex-col">
        <TopBar user={session.user} />
        <main className="flex-1 p-6 bg-muted/20">
          <SessionContext.Provider value={{ ...session, switchOrg }}>
            <Outlet />
          </SessionContext.Provider>
        </main>
      </div>
    </div>
  );
}

import { createContext, useContext } from 'react';
const SessionContext = createContext<Session | null>(null);
export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession used outside AppShell');
  return s;
}
